import { ColumnDef } from "@tanstack/react-table";
import { z, ZodObject, ZodRawShape } from "zod";
import { useTranslation } from "react-i18next";
import LicensePlate from "license-plate";
import licencePlateSelectBg from "../../../general/fields/modules/commons/licencePlateSelectField/assets/licencePlateSelectBg.svg";
import { requestTiresResponseModel } from "../models/requestModel";
import React from "react";
import { EntitySelectField } from "../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { positionLabelSchema } from "../../../storage/position/domain/schemas/positionLabelSchema";
import edit from "../../../planning/packingSlip/modules/features/assets/pen-to-square-solid.svg";

export const RequestTableColumns: ColumnDef<z.infer<ZodObject<ZodRawShape>>>[] =
  [
    {
      id: "source.car.licence",
      accessorKey: "source.car.licence",
      header: "licence",
      enableSorting: false,
      cell: ({ row }) => {
        const { t } = useTranslation("common");
        const plate = new LicensePlate(row.original.source?.car?.licence ?? "");

        return (
          <div className={"d-flex align-items-center gap-2"}>
            {row.original.source?.car?.licence && (
              <div
                className={"tireset-table-licence-plate"}
                style={{
                  background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
                }}
              >
                <div>
                  {plate.valid()
                    ? plate.pretty()
                    : row.original.source?.car?.licence}
                </div>
              </div>
            )}
            <div>{row.original.source?.car?.label}</div>

            {row.original.freestock && (
              <div>
                <span className="badge text-bg-secondary me-2">
                  {t("Free stock")}
                </span>

                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
                {row.original.requestTires?.map(
                  (tire: requestTiresResponseModel) => (
                    <span key={tire.id}>
                      {t(tire.tire.car_position || "Unknown")}{" "}
                    </span>
                  ),
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "source.type",
      header: "type",
      enableSorting: false,
      cell: ({ row }) => {
        const label = row.original.source?.type;
        const { t } = useTranslation("common");

        return t(label);
      },
    },
    {
      id: "relation.name",
      accessorKey: "relation.name",
      header: "relation",
    },
    {
      enableSorting: false,
      accessorKey: "source.sleep_set",
      header: "sleep_set",
      cell: ({ row }) => {
        const label = row.original.source?.sleep_set ? "Yes" : "No";
        const { t } = useTranslation("common");

        return t(label);
      },
    },
    {
      accessorKey: "state",
      header: "state",
      cell: ({ row }) => {
        const type = row?.original.type;
        const state = row?.original.state;
        const { t } = useTranslation("common");

        if (state === undefined) return;

        if (state === "concept" || state === "submitted") {
          return (
            <span className={"badge bg-" + state}>
              {t(state)} - {t(type)}
            </span>
          );
        }

        return <span className={"badge bg-" + state}>{t(state)}</span>;
      },
    },
    {
      accessorKey: "pickup_and_delivery_date",
      header: "b_o_date",
      enableSorting: true,
      cell: ({ row }) => {
        const dateString = row?.original.pickup_and_delivery_date;

        if (
          dateString === undefined ||
          dateString === "" ||
          dateString === null
        )
          return;

        const date = new Date(dateString);
        const formattedDate = new Intl.DateTimeFormat("en-GB")
          .format(date)
          .replace(/\//g, "-");

        return <span>{formattedDate}</span>;
      },
    },
    {
      enableSorting: true,
      accessorKey: "in_out_storage",
      header: "in_storage",
      cell: ({ row }) => {
        const type = row?.original.type;
        if (type !== "pickup") {
          return;
        }
        const dateString = row?.original.in_out_storage;

        if (
          dateString === undefined ||
          dateString === "" ||
          dateString === null
        )
          return;

        const date = new Date(dateString);
        const formattedDate = new Intl.DateTimeFormat("en-GB")
          .format(date)
          .replace(/\//g, "-");

        return <span>{formattedDate}</span>;
      },
    },
    {
      enableSorting: true,
      accessorKey: "in_out_storage",
      header: "out_storage",
      cell: ({ row }) => {
        const type = row?.original.type;
        if (type !== "deliver") {
          return;
        }
        const dateString = row?.original.in_out_storage;

        if (
          dateString === undefined ||
          dateString === "" ||
          dateString === null
        )
          return;

        const date = new Date(dateString);
        const formattedDate = new Intl.DateTimeFormat("en-GB")
          .format(date)
          .replace(/\//g, "-");

        return <span>{formattedDate}</span>;
      },
    },
    {
      id: "source.position.id",
      accessorKey: "source.position.id",
      header: "location",
      enableSorting: false,
      meta: {
        columnAction: "disabled",
      },
      cell: ({ getValue, row, column }) => {
        const initialValue = getValue();
        const [value, setValue] = React.useState(initialValue);
        const [isEdit, setIsEdit] = React.useState(false);
        const [hasChanged, setHasChanged] = React.useState(false);

        // Reset edit mode when row data changes (e.g., due to filtering/searching)
        React.useEffect(() => {
          setIsEdit(false);
          setHasChanged(false);
          // Also reset value to current row's value when row changes
          setValue(initialValue);
        }, [row.id, row.index, initialValue]);

        const changeValue = (newValue: number | null) => {
          if (!hasChanged) {
            return;
          }
          if (newValue === value) {
            return;
          }

          setValue(newValue);
          if (column.columnDef.meta?.updateData !== undefined) {
            column.columnDef.meta?.updateData(row, newValue);
          }
        };

        const position = row?.original?.source?.position;
        if (!isEdit) {
          return (
            <a
              href={"#"}
              onClick={(e) => {
                e.preventDefault();
                setIsEdit(true);
              }}
              className="position-edit-link"
            >
              <span>{position?.label}</span>
              <img src={edit} width={16} alt={"edit"} className="edit-icon" />
            </a>
          );
        }

        return (
          <EntitySelectField
            entity="Position"
            prefix="positions"
            labelPrefix={"label"}
            key={"position-" + row.index + "-" + row.id}
            name={"position-" + row.index}
            clearable={true}
            value={value as string}
            defaultValueLabel={position?.label}
            onChange={(option) => {
              setHasChanged(true);

              const newValue = option
                ? (option as { value: number }).value
                : null;
              changeValue(newValue);
            }}
            schema={positionLabelSchema}
            limit={100}
          />
        );
      },
    },
  ];
